/* Scene.jsx — Hero centerpiece: edificio + pila de monedas + bombero en la ventana.
   La pila de monedas sube por el costado derecho del edificio a medida que
   avanza el % recaudado; marcadores de etapa (25/50/75/100%) a la derecha.
   Al llegar al 100% se dispara el lienzo de victoria + fuegos artificiales. */

import React from 'react'

const { useEffect, useMemo, useRef, useState } = React;

// ─── Geometría del edificio ────────────────────────────────────
const B_BASE_X  = 130;
const B_TOP_Y   = 130;
const B_W       = 200;
const B_FLOOR_H = 50;
const B_FLOORS  = 11;
const B_H       = B_FLOORS * B_FLOOR_H + 60;
const B_SIDE    = 22;

// Posición exacta de la ventana de rescate (piso 0, apt 1)
const RESCUE_FLOOR = 0;
const RESCUE_APT   = 1;

// ─── Pila vertical de monedas — al costado derecho del edificio ──
// La altura de la pila refleja el % recaudado.
const STACK_X        = 368;
const STACK_BOTTOM_Y = 738;
const STACK_TOP_Y    = 148;

// ─── Etapas de la campaña (en % de la meta, sin mencionar metros) ──
const CAMPAIGN_STAGES = [
  { frac: 0.25, label: "25%",  note: "1ª etapa",    color: "#c8932a" },
  { frac: 0.50, label: "50%",  note: "Mitad",       color: "#d4a83b" },
  { frac: 0.75, label: "75%",  note: "Recta final", color: "#e0b84a" },
  { frac: 1.00, label: "100%", note: "¡META!",      color: "#f4c869" },
];

const TOTAL_COINS = 32;

// ─── Posición de cada moneda en la pila vertical ──────────────
function stairPath(i, total) {
  const t = i / Math.max(total - 1, 1);
  const y = STACK_BOTTOM_Y - t * (STACK_BOTTOM_Y - STACK_TOP_Y);
  return { x: STACK_X, y, tilt: 0 };
}

// ─── Gradientes SVG ────────────────────────────────────────────
function CoinDefs() {
  return (
    <defs>
      {/* Moneda $500 bimetálica */}
      <radialGradient id="csRim" cx="0.28" cy="0.20" r="0.72" gradientUnits="objectBoundingBox">
        <stop offset="0%"   stopColor="#eeeeF4" />
        <stop offset="32%"  stopColor="#c4c4d0" />
        <stop offset="68%"  stopColor="#8a8a98" />
        <stop offset="100%" stopColor="#424250" />
      </radialGradient>
      <radialGradient id="csCore" cx="0.32" cy="0.20" r="0.70" gradientUnits="objectBoundingBox">
        <stop offset="0%"   stopColor="#fff8b0" />
        <stop offset="28%"  stopColor="#f0bc28" />
        <stop offset="58%"  stopColor="#bf7c0e" />
        <stop offset="100%" stopColor="#6a3c08" />
      </radialGradient>
      <linearGradient id="csEdge" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="#767682" />
        <stop offset="100%" stopColor="#1e1e28" />
      </linearGradient>

      {/* Edificio */}
      <radialGradient id="window-glow" cx="0.5" cy="0.5" r="0.7">
        <stop offset="0%"   stopColor="#ffe4a8" stopOpacity="1" />
        <stop offset="100%" stopColor="#f4a83a" stopOpacity="0.4" />
      </radialGradient>
      <radialGradient id="smoke-glow" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0%"   stopColor="#ff7e54" stopOpacity="0.4" />
        <stop offset="100%" stopColor="#ff7e54" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="building-face" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%"   stopColor="#5a564a" />
        <stop offset="35%"  stopColor="#4d4a40" />
        <stop offset="70%"  stopColor="#383530" />
        <stop offset="100%" stopColor="#26241f" />
      </linearGradient>
      <linearGradient id="building-side" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%"  stopColor="#1c1a16" />
        <stop offset="100%" stopColor="#2e2a23" />
      </linearGradient>
      <linearGradient id="building-warm-wash" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"  stopColor="#f4a83a" stopOpacity="0.16" />
        <stop offset="60%" stopColor="#f4a83a" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="building-slab" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="#787463" />
        <stop offset="100%" stopColor="#3a3730" />
      </linearGradient>
      <pattern id="concrete-noise" x="0" y="0" width="3" height="200" patternUnits="userSpaceOnUse">
        <line x1="0" y1="0" x2="0" y2="200" stroke="rgba(255,255,255,.05)" strokeWidth="0.5" />
      </pattern>
      <radialGradient id="night-haze" cx="0.5" cy="0.6" r="0.6">
        <stop offset="0%"   stopColor="#3a2c1a" stopOpacity="0.55" />
        <stop offset="60%"  stopColor="#1a1410" stopOpacity="0.2" />
        <stop offset="100%" stopColor="#0a0907" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="window-rescue" cx="0.5" cy="0.4" r="0.7">
        <stop offset="0%"   stopColor="#ffb27e" stopOpacity="1" />
        <stop offset="50%"  stopColor="#f47a3a" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#c8472a" stopOpacity="0.7" />
      </radialGradient>
      <linearGradient id="lobby-glow" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="#f4c869" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#c8932a" stopOpacity="0.6" />
      </linearGradient>

      {/* Victoria */}
      <linearGradient id="banner-bg-grad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="#0d0800" />
        <stop offset="100%" stopColor="#080400" />
      </linearGradient>
      <linearGradient id="banner-stripe" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="#f0c054" />
        <stop offset="100%" stopColor="#b07818" />
      </linearGradient>
      <radialGradient id="victory-halo" cx="0.5" cy="0.5" r="0.6">
        <stop offset="0%"   stopColor="#E8A93B" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#E8A93B" stopOpacity="0" />
      </radialGradient>
    </defs>
  );
}

// ─── Moneda individual ($500 pesos chilena, bimetálica) ──────
function Coin({ x, y, tilt, isNew, isVictory, scale = 1 }) {
  const rx  = 38 * scale;
  const ry  = 10.5 * scale;
  const t   = 5.5 * scale;
  const irx = rx - 8.5 * scale;
  const iry = ry - 3.0 * scale;

  return (
    <g transform={`translate(${x},${y}) rotate(${tilt})`}
       style={isNew ? { animation: "coinDrop 0.9s cubic-bezier(.22,.82,.3,1.15) both", transformBox: "fill-box" } : {}}>

      {/* Sombra difusa */}
      <ellipse cx="1" cy={t + 2.5} rx={rx + 5} ry={ry + 2.5} fill="rgba(0,0,0,.36)" />

      {/* Canto — da el volumen 3D */}
      <path d={`M ${-rx},0 A ${rx} ${ry} 0 0 0 ${rx},0 L ${rx},${t} A ${rx} ${ry} 0 0 1 ${-rx},${t} Z`}
        fill="url(#csEdge)" />
      {Array.from({ length: 20 }, (_, i) => {
        const cxi = -rx + (i + 0.5) * (rx * 2) / 20;
        return <line key={i} x1={cxi} y1="0.4" x2={cxi} y2={t - 0.3}
          stroke="rgba(255,255,255,.18)" strokeWidth="0.8" />;
      })}
      <path d={`M ${-rx * 0.88},0 A ${rx} ${ry} 0 0 0 ${rx * 0.88},0 L ${rx * 0.88},${t * 0.28} A ${rx} ${ry} 0 0 1 ${-rx * 0.88},${t * 0.28} Z`}
        fill="rgba(210,210,220,.22)" />

      {/* Cara frontal: anillo plateado */}
      <ellipse cx="0" cy="0" rx={rx} ry={ry} fill="url(#csRim)" />

      {/* Surco bimetálico */}
      <ellipse cx="0" cy="0" rx={irx + 1.8 * scale} ry={iry + 0.7 * scale}
        fill="rgba(12,8,2,.60)" />

      {/* Núcleo dorado */}
      <ellipse cx="0" cy="0" rx={irx} ry={iry} fill="url(#csCore)" />

      <text x="0" y={1.8 * scale} textAnchor="middle"
        fontFamily="Anton, Impact, sans-serif"
        fontSize={6.8 * scale}
        fill="rgba(32,16,2,.92)">500</text>

      {/* Reflejo especular */}
      <ellipse cx={-rx * 0.20} cy={-ry * 0.50} rx={rx * 0.52} ry={ry * 0.36}
        fill="rgba(255,255,255,.40)" />
      <ellipse cx={-rx * 0.35} cy={-ry * 0.65} rx={rx * 0.15} ry={ry * 0.18}
        fill="rgba(255,255,255,.55)" />

      {isVictory && (
        <ellipse cx="0" cy="0" rx={rx + 10} ry={ry + 5}
          fill="rgba(252,220,100,.28)"
          style={{ animation: "scenePulse 1.6s ease-in-out infinite" }} />
      )}
    </g>
  );
}

// ─── Marcadores de etapa — labels a la derecha del SVG (en %) ──
function StageMarkers({ visible }) {
  const LABEL_X = 488;
  return (
    <g>
      {CAMPAIGN_STAGES.map((st, si) => {
        const idx = Math.round(st.frac * TOTAL_COINS) - 1;
        const { x, y } = stairPath(idx, TOTAL_COINS);
        const reached = visible > idx;
        const c = reached ? st.color : "rgba(232,169,59,.22)";
        return (
          <g key={si} opacity={reached ? 1 : 0.40}>
            <line x1={x + 42} y1={y} x2={LABEL_X - 6} y2={y}
              stroke={c} strokeWidth={reached ? "0.9" : "0.5"}
              strokeDasharray="3 3.5" />
            <circle cx={x} cy={y} r={reached ? 5.5 : 4}
              fill={reached ? st.color : "rgba(232,169,59,.15)"}
              stroke={reached ? "rgba(255,255,255,.5)" : "rgba(232,169,59,.3)"}
              strokeWidth="1" />
            <text x={LABEL_X} y={y - 4} textAnchor="start"
              fontFamily="JetBrains Mono, monospace"
              fontSize={reached ? "9" : "8"} fontWeight={reached ? "700" : "400"}
              fill={c} dominantBaseline="middle">
              {st.label}
            </text>
            <text x={LABEL_X} y={y + 8} textAnchor="start"
              fontFamily="Manrope, sans-serif" fontSize="7"
              fill={c} opacity="0.78" dominantBaseline="middle">
              {st.note}
            </text>
          </g>
        );
      })}
    </g>
  );
}

// ─── Fuego artificial ──────────────────────────────────────────
function FireworkBurst({ cx, cy, colors, delay, dur = "2.5s" }) {
  const N = 20;
  return (
    <g>
      {Array.from({ length: N }, (_, i) => {
        const a    = (i / N) * Math.PI * 2 + delay * 0.9;
        const dist = 38 + (i % 5) * 13;
        const dx   = Math.cos(a) * dist;
        const dy   = Math.sin(a) * dist;
        const col  = colors[i % colors.length];
        return (
          <circle key={i} cx={cx} cy={cy} r="0" fill={col} opacity="0">
            <animate attributeName="opacity"
              values="0;0;1;0.9;0"   keyTimes="0;0.07;0.15;0.65;1"
              dur={dur} begin={`${delay}s`} repeatCount="indefinite" />
            <animate attributeName="cx"
              values={`${cx};${cx};${cx + dx}`} keyTimes="0;0.08;1"
              dur={dur} begin={`${delay}s`} repeatCount="indefinite"
              calcMode="spline" keySplines="0 0 1 1;0.15 0.8 0.28 1" />
            <animate attributeName="cy"
              values={`${cy};${cy};${cy + dy}`} keyTimes="0;0.08;1"
              dur={dur} begin={`${delay}s`} repeatCount="indefinite"
              calcMode="spline" keySplines="0 0 1 1;0.15 0.8 0.28 1" />
            <animate attributeName="r"
              values="0;0;3;1.2;0"   keyTimes="0;0.08;0.18;0.65;1"
              dur={dur} begin={`${delay}s`} repeatCount="indefinite" />
          </circle>
        );
      })}
      <circle cx={cx} cy={cy} r="0" fill="white" opacity="0">
        <animate attributeName="r"       values="0;0;24;0"    keyTimes="0;0.07;0.16;0.36" dur={dur} begin={`${delay}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" values="0;0;0.9;0"   keyTimes="0;0.07;0.12;0.36" dur={dur} begin={`${delay}s`} repeatCount="indefinite" />
      </circle>
      <line x1={cx} x2={cx} y1={cy + 70} y2={cy + 70} stroke={colors[0]} strokeWidth="2" strokeLinecap="round" opacity="0">
        <animate attributeName="y1"      values={`${cy + 70};${cy};${cy};${cy + 70};${cy + 70}`} keyTimes="0;0.07;0.09;0.5;1" dur={dur} begin={`${delay}s`} repeatCount="indefinite" />
        <animate attributeName="y2"      values={`${cy + 70};${cy + 70};${cy};${cy + 70};${cy + 70}`} keyTimes="0;0.07;0.09;0.5;1" dur={dur} begin={`${delay}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" values="0;0.9;0;0;0"                               keyTimes="0;0.03;0.10;0.5;1" dur={dur} begin={`${delay}s`} repeatCount="indefinite" />
      </line>
    </g>
  );
}

function Fireworks() {
  const bursts = [
    { cx: 82,  cy: 245, colors: ["#E8A93B", "#fce490", "#fff8e0"], delay: 0.0,  dur: "2.7s" },
    { cx: 168, cy: 185, colors: ["#E63946", "#ff7a7a", "#ffffff"], delay: 0.55, dur: "2.3s" },
    { cx: 254, cy: 210, colors: ["#5bc8f5", "#aae8ff", "#ffffff"], delay: 1.1,  dur: "2.8s" },
    { cx: 315, cy: 158, colors: ["#88ef7a", "#c0ffb0", "#ffffff"], delay: 0.3,  dur: "2.4s" },
    { cx: 100, cy: 298, colors: ["#E8A93B", "#ffddaa", "#E63946"], delay: 1.55, dur: "2.2s" },
    { cx: 196, cy: 138, colors: ["#ffffff", "#E8A93B", "#f0d0ff"], delay: 0.88, dur: "3.0s" },
    { cx: 58,  cy: 182, colors: ["#f5aaff", "#ffffff", "#E8A93B"], delay: 2.0,  dur: "2.6s" },
    { cx: 298, cy: 272, colors: ["#E8A93B", "#E63946", "#5bc8f5"], delay: 1.75, dur: "2.4s" },
  ];
  return <g>{bursts.map((b, i) => <FireworkBurst key={i} {...b} />)}</g>;
}

// ─── Lienzo de victoria — cubre todo el edificio ──────────────
function VictoryBanner({ active }) {
  if (!active) return null;
  const bx = B_BASE_X, by = B_TOP_Y, bw = B_W, bh = B_H;
  const mid = by + bh * 0.46;

  return (
    <g style={{ animation: "bannerDrop 2s cubic-bezier(.2,.85,.3,1.05) forwards" }}>
      <rect x={bx} y={by} width={bw} height={bh} fill="url(#banner-bg-grad)" opacity="0.96" />
      <rect x={bx} y={by} width={bw} height={bh} fill="none" stroke="#E8A93B" strokeWidth="2" />

      <rect x={bx} y={by} width={bw} height="18" fill="url(#banner-stripe)" />
      <rect x={bx} y={by + bh - 14} width={bw} height="14" fill="url(#banner-stripe)" opacity="0.75" />

      <line x1={bx + 5} y1={by + 18} x2={bx + 5} y2={by + bh - 14} stroke="rgba(232,169,59,.12)" strokeWidth="1" />
      <line x1={bx + bw - 5} y1={by + 18} x2={bx + bw - 5} y2={by + bh - 14} stroke="rgba(232,169,59,.12)" strokeWidth="1" />

      <line x1={bx + 14} y1={mid - 52} x2={bx + bw - 14} y2={mid - 52}
        stroke="rgba(232,169,59,.3)" strokeWidth="0.8" />
      <line x1={bx + 14} y1={mid + 60} x2={bx + bw - 14} y2={mid + 60}
        stroke="rgba(232,169,59,.3)" strokeWidth="0.8" />

      <text x={bx + bw / 2} y={mid - 34}
        textAnchor="middle" fontFamily="Manrope, sans-serif"
        fontSize="11" fontWeight="600" fill="rgba(232,169,59,.7)" letterSpacing="0.1em">
        CON TU APORTE
      </text>

      <text x={bx + bw / 2} y={mid}
        textAnchor="middle" fontFamily="Anton, sans-serif"
        fontSize="26" fill="#f4c869" letterSpacing="0.03em">
        ¡LO LOGRAMOS!
      </text>

      <text x={bx + bw / 2} y={mid + 20}
        textAnchor="middle" fontFamily="JetBrains Mono, monospace"
        fontSize="8.5" fill="rgba(232,169,59,.55)" letterSpacing="0.14em">
        BOMBEROS QUILPUÉ
      </text>

      {[-54, -28, 0, 28, 54].map((dx, i) => (
        <text key={i}
          x={bx + bw / 2 + dx} y={mid + 42}
          textAnchor="middle" fontFamily="sans-serif"
          fontSize={i === 2 ? "15" : "10"} fill="#E8A93B" opacity={i === 2 ? 1 : 0.55}>
          ★
        </text>
      ))}

      {Array.from({ length: 14 }, (_, i) => {
        const fx = bx + (i + 0.5) * (bw / 14);
        return (
          <line key={i} x1={fx} y1={by + bh} x2={fx + (i % 2 ? 2.5 : -2.5)} y2={by + bh + 13}
            stroke="#a87016" strokeWidth="2" strokeLinecap="round" />
        );
      })}
    </g>
  );
}

// ─── Persona en la ventana de rescate (siempre visible, encima de todo) ──
function RescuePerson({ isVictory }) {
  const APTS = 3;
  const fy = B_TOP_Y + RESCUE_FLOOR * B_FLOOR_H;
  const apX = B_BASE_X + RESCUE_APT * (B_W / APTS) + 6;
  const apW = B_W / APTS - 12;
  const cx = apX + apW * 0.5;

  if (!isVictory) {
    return (
      <g>
        <ellipse cx={cx} cy={fy + 18} rx="3.5" ry="3.8" fill="rgba(10,8,5,.9)" />
        <rect x={cx - 4} y={fy + 21} width="8" height="14" rx="2" fill="rgba(10,8,5,.9)" />
        <rect x={cx + 4} y={fy + 14} width="2" height="11" rx="1" fill="rgba(10,8,5,.9)"
          transform={`rotate(22 ${cx + 5} ${fy + 20})`} />
      </g>
    );
  }

  return (
    <g>
      <ellipse cx={cx} cy={fy + 22} rx="22" ry="26"
        fill="rgba(244,200,105,.18)"
        style={{ animation: "scenePulse 1.4s ease-in-out infinite" }} />
      <ellipse cx={cx} cy={fy + 18} rx="3.5" ry="3.8" fill="rgba(10,8,5,.9)" />
      <rect x={cx - 4} y={fy + 21} width="8" height="14" rx="2" fill="rgba(10,8,5,.9)" />
      <rect x={cx - 6} y={fy + 13} width="2" height="11" rx="1" fill="rgba(10,8,5,.9)"
        transform={`rotate(-32 ${cx - 5} ${fy + 20})`} />
      <rect x={cx + 4} y={fy + 13} width="2" height="11" rx="1" fill="rgba(10,8,5,.9)"
        transform={`rotate(32 ${cx + 5} ${fy + 20})`} />
    </g>
  );
}

// ─── Edificio (sin la persona en la ventana de rescate) ───────
function Building({ progress = 0, isVictory = false }) {
  const FLOORS = B_FLOORS, APTS = 3;
  const baseX = B_BASE_X, topY = B_TOP_Y, floorH = B_FLOOR_H;
  const bW = B_W, bH = B_H, bottomY = topY + bH;
  const side = B_SIDE;

  const litBelow = isVictory ? FLOORS + 1 : Math.floor(progress * (FLOORS + 1));
  function aptLit(fi, ai) {
    if (isVictory) return true;
    const fb = FLOORS - 1 - fi;
    if (fb >= litBelow) return false;
    return ((fi * 7 + ai * 3) % 5) !== 2;
  }

  return (
    <g>
      <rect x={baseX - 30} y={topY - 60} width={bW + side + 60} height={bH + 100}
        fill="url(#night-haze)" opacity="0.5" />

      <g opacity="0.4">
        {[[-80, 540], [-70, 560], [-60, 545], [-50, 565], [-40, 555],
          [bW + side + 30, 530], [bW + side + 45, 555], [bW + side + 65, 545], [bW + side + 85, 560]
        ].map(([dx, dy], i) => (
          <rect key={i} x={baseX + dx} y={topY + dy} width="2" height="2" fill="#f4c869"
            opacity={0.5 + (i % 3) * 0.15} />
        ))}
      </g>

      <polygon
        points={`${baseX + bW},${topY} ${baseX + bW + side},${topY - 6} ${baseX + bW + side},${bottomY - 6} ${baseX + bW},${bottomY}`}
        fill="url(#building-side)" />
      {Array.from({ length: 3 }, (_, i) => (
        <line key={`sv${i}`}
          x1={baseX + bW + (i + 1) * (side / 4)} y1={topY - (i + 1)}
          x2={baseX + bW + (i + 1) * (side / 4)} y2={bottomY - (i + 1)}
          stroke="rgba(255,255,255,.04)" strokeWidth="0.4" />
      ))}

      <rect x={baseX} y={topY} width={bW} height={bH} fill="url(#building-face)" />
      <rect x={baseX} y={topY} width={bW} height={bH * 0.5} fill="url(#building-warm-wash)" />
      <rect x={baseX} y={topY} width="3" height={bH} fill="rgba(255,255,255,.16)" />
      <rect x={baseX + bW - 2} y={topY} width="2" height={bH} fill="rgba(0,0,0,.5)" />
      <rect x={baseX} y={topY} width={bW} height={bH} fill="url(#concrete-noise)" opacity="0.7" />

      {Array.from({ length: APTS + 1 }, (_, i) => {
        const cx = baseX + i * bW / APTS;
        return (
          <g key={`col${i}`}>
            <rect x={cx - 2.5} y={topY} width="5" height={bH - 6} fill="rgba(0,0,0,.5)" />
            <rect x={cx - 0.5} y={topY} width="1" height={bH - 6} fill="rgba(255,255,255,.10)" />
          </g>
        );
      })}

      {Array.from({ length: FLOORS + 1 }, (_, i) => (
        <g key={`sl${i}`}>
          <rect x={baseX - 2} y={topY + i * floorH - 2} width={bW + 4} height="4" fill="url(#building-slab)" />
          <rect x={baseX - 2} y={topY + i * floorH - 2} width={bW + 4} height="1" fill="rgba(255,255,255,.18)" />
        </g>
      ))}

      {Array.from({ length: FLOORS }, (_, fi) => {
        const fy = topY + fi * floorH;
        return Array.from({ length: APTS }, (_, ai) => {
          const lit = aptLit(fi, ai);
          const isR = fi === RESCUE_FLOOR && ai === RESCUE_APT;
          const apX = baseX + ai * bW / APTS + 6;
          const apW = bW / APTS - 12;
          const winH = floorH - 18;
          const balH = 8;
          const on = lit || isR;
          const glow = isR ? "url(#window-rescue)" : "url(#window-glow)";
          return (
            <g key={`apt-${fi}-${ai}`}>
              <rect x={apX} y={fy + 6} width={apW} height={winH} fill="#0a0a0c" />
              <rect x={apX + 1} y={fy + 6} width={apW - 2} height={winH - balH}
                fill={on ? glow : "#0b0c10"}
                style={on ? { transition: "fill .8s ease" } : undefined} />
              <rect x={apX + 1} y={fy + 6 + winH - balH} width={apW - 2} height={balH - 1}
                fill={on ? glow : "#0b0c10"} opacity={on ? 0.75 : 1} />
              <line x1={apX + apW / 2} y1={fy + 6} x2={apX + apW / 2} y2={fy + 6 + winH}
                stroke="rgba(0,0,0,.5)" strokeWidth="1" />
              <rect x={apX - 0.5} y={fy + 5.5} width={apW + 1} height="1.5" fill="rgba(0,0,0,.7)" />
              <rect x={apX - 2} y={fy + 6 + winH} width={apW + 4} height="8"
                fill={on ? "rgba(244,200,105,.18)" : "rgba(255,255,255,.04)"}
                stroke="rgba(255,255,255,.07)" strokeWidth="0.5" />
              {on && (
                <rect x={apX - 4} y={fy + 2} width={apW + 8} height={winH + 12}
                  fill={glow} opacity="0.18" style={{ pointerEvents: "none" }} />
              )}
            </g>
          );
        });
      })}

      {(() => {
        const lY = topY + FLOORS * floorH, lH = 60, gY = lY + 10, gH = lH - 18;
        return (
          <g>
            <rect x={baseX} y={lY} width={bW} height={lH} fill="#3d3a32" />
            <rect x={baseX - 2} y={lY} width={bW + 4} height="6" fill="#26241f" />
            <rect x={baseX - 2} y={lY} width={bW + 4} height="1.5" fill="rgba(255,255,255,.22)" />
            <rect x={baseX + 10} y={gY} width={bW - 20} height={gH} fill="url(#lobby-glow)" />
            <ellipse cx={baseX + bW / 2} cy={lY + lH + 4} rx={bW * 0.55} ry="8" fill="#f4a83a" opacity="0.18" />
            {Array.from({ length: 5 }, (_, i) => {
              const mx = baseX + 10 + i * (bW - 20) / 4;
              return <line key={`lm${i}`} x1={mx} y1={gY} x2={mx} y2={gY + gH}
                stroke="rgba(0,0,0,.65)" strokeWidth="1.2" />;
            })}
            <rect x={baseX + bW / 2 - 18} y={gY + 4} width="36" height={gH - 6}
              fill="rgba(11,10,8,.55)" stroke="rgba(232,169,59,.7)" strokeWidth="0.8" />
            <line x1={baseX + bW / 2} y1={gY + 4} x2={baseX + bW / 2} y2={gY + gH - 2}
              stroke="rgba(232,169,59,.55)" strokeWidth="0.8" />
            <rect x={baseX - 6} y={lY + lH - 4} width={bW + side + 12} height="4" fill="#1a1812" />
          </g>
        );
      })()}

      <rect x={baseX - 4} y={topY - 10} width={bW + 8} height="10" fill="#3a3730" />
      <rect x={baseX - 4} y={topY - 10} width={bW + 8} height="1.5" fill="rgba(255,255,255,.22)" />
      <rect x={baseX + 20} y={topY - 32} width="36" height="22" fill="#2a2620" />
      <rect x={baseX + bW - 80} y={topY - 26} width="50" height="16" fill="#2e2a23" />
      <line x1={baseX + bW - 30} y1={topY - 8} x2={baseX + bW - 30} y2={topY - 56}
        stroke="#1a1a1e" strokeWidth="1.5" />
      {!isVictory && (
        <>
          <circle cx={baseX + bW - 30} cy={topY - 56} r="2.2" fill="#E63946" opacity="0.85">
            <animate attributeName="opacity" values="0.85;0.15;0.85" dur="1.6s" repeatCount="indefinite" />
          </circle>
          <circle cx={baseX + bW - 30} cy={topY - 56} r="6" fill="rgba(230,57,70,.25)">
            <animate attributeName="r" values="6;9;6" dur="1.6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values=".4;0;.4" dur="1.6s" repeatCount="indefinite" />
          </circle>
          <ellipse cx={baseX + (RESCUE_APT + 0.5) * (bW / APTS)} cy={B_TOP_Y + 20} rx="90" ry="60"
            fill="url(#smoke-glow)" opacity="0.8" />
        </>
      )}
      {isVictory && (
        <ellipse cx={baseX + bW / 2} cy={topY + bH * 0.3} rx={bW * 0.85} ry={bH * 0.5}
          fill="url(#victory-halo)"
          style={{ animation: "scenePulse 2s ease-in-out infinite" }} />
      )}
    </g>
  );
}

// ─── Spark burst al llegar donación ───────────────────────────
function SparkBurst({ x, y, seed = 0 }) {
  const pts = Array.from({ length: 18 }, (_, i) => {
    const a = (i / 18) * Math.PI * 2 + (seed % 7) * 0.13;
    const dist = 54 + ((i * 7 + seed * 3) % 36);
    const colors = ["#f4c869", "#E8A93B", "#fff5d6", "#E63946", "#ffb27e"];
    return {
      dx: Math.cos(a) * dist, dy: Math.sin(a) * dist - 16,
      color: colors[(i + seed) % colors.length],
      delay: (i % 6) * 0.04, size: 2 + (i % 3),
    };
  });
  return (
    <g transform={`translate(${x},${y})`} style={{ pointerEvents: "none" }}>
      <circle cx="0" cy="0" r="6" fill="none" stroke="#E8A93B" strokeWidth="2" opacity="0.9">
        <animate attributeName="r"       from="6"   to="54"  dur="0.85s" fill="freeze" />
        <animate attributeName="opacity" from="0.9" to="0"   dur="0.85s" fill="freeze" />
      </circle>
      <circle cx="0" cy="0" r="13" fill="#fff5d6" opacity="0.85">
        <animate attributeName="r"       from="13"  to="0"   dur="0.3s" fill="freeze" />
        <animate attributeName="opacity" from="0.85" to="0"  dur="0.3s" fill="freeze" />
      </circle>
      {pts.map((p, i) => (
        <circle key={i} cx="0" cy="0" r={p.size} fill={p.color}>
          <animate attributeName="cx" from="0" to={p.dx} dur="1.0s" begin={`${p.delay}s`} fill="freeze"
            calcMode="spline" keyTimes="0;1" keySplines="0.2 0.7 0.4 1" />
          <animate attributeName="cy" from="0" to={p.dy + 72} dur="1.0s" begin={`${p.delay}s`} fill="freeze"
            calcMode="spline" keyTimes="0;1" keySplines="0.3 0.4 0.7 1" />
          <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.05;0.68;1"
            dur="1.0s" begin={`${p.delay}s`} fill="freeze" />
          <animate attributeName="r" from={p.size} to="0.4" dur="1.0s" begin={`${p.delay}s`} fill="freeze" />
        </circle>
      ))}
    </g>
  );
}

// ─── Scene principal ───────────────────────────────────────────
export function Scene({ progress = 0.29, lastDonor = null, theme = "night" }) {
  const isVict = progress >= 1;

  const targetVisible = Math.max(1, Math.round(Math.min(progress, 1) * TOTAL_COINS));
  const [visible, setVisible] = useState(targetVisible);
  const prevVisible = useRef(visible);

  useEffect(() => {
    if (targetVisible === visible) return;
    if (targetVisible > visible) {
      const t = setTimeout(() => {
        prevVisible.current = visible;
        setVisible((v) => v + 1);
      }, 120);
      return () => clearTimeout(t);
    } else {
      prevVisible.current = targetVisible;
      setVisible(targetVisible);
    }
  }, [targetVisible, visible]);

  const coins = useMemo(() =>
    Array.from({ length: TOTAL_COINS }, (_, i) => ({
      ...stairPath(i, TOTAL_COINS), i,
    })), []);

  const topCoin = coins[Math.min(visible - 1, TOTAL_COINS - 1)];

  return (
    <div className="scene">
      <div className="scene-stars" />

      <div className="scene-hud">
        <div className="scene-hud-tag">
          <span className="live" />
          {isVict
            ? "¡Meta alcanzada!"
            : `Llevamos ${(progress * 100).toFixed(1).replace(".", ",")}% de la meta`}
        </div>
        <div className="scene-hud-tag" style={{ background: "rgba(11,10,8,.6)" }}>
          Meta 800.000.000
        </div>
      </div>

      <svg viewBox="0 0 600 770" preserveAspectRatio="xMidYMid slice"
        style={{ width: "100%", height: "100%", display: "block" }}>
        <CoinDefs />

        {/* Suelo — flush contra la base del edificio, sin espacio muerto */}
        <rect x="0" y="740" width="600" height="30" fill="#08070a" />
        <line x1="0" y1="740" x2="600" y2="740" stroke="rgba(255,255,255,.06)" strokeWidth="1" />

        {/* 1 — Fuegos artificiales (detrás del edificio) */}
        {isVict && <Fireworks />}

        {/* 2 — Edificio */}
        <Building progress={visible / TOTAL_COINS} isVictory={isVict} />

        {/* 3 — Monedas apiladas (encima del edificio) */}
        {coins.map((c) => {
          const isVisible = c.i < visible;
          const isNew = c.i === visible - 1 && c.i >= prevVisible.current;
          return isVisible ? (
            <Coin key={c.i} x={c.x} y={c.y} tilt={c.tilt}
              isNew={isNew} isVictory={isVict} />
          ) : (
            <ellipse key={c.i}
              cx={c.x} cy={c.y} rx="34" ry="8"
              fill="none"
              stroke="rgba(232,169,59,.14)"
              strokeWidth="0.7"
              strokeDasharray="2 4"
              transform={`rotate(${c.tilt} ${c.x} ${c.y})`}
              style={{ animation: "sceneGhostPulse 3s ease-in-out infinite" }}
            />
          );
        })}

        {/* 4 — Lienzo de victoria (cae encima de monedas y edificio) */}
        <VictoryBanner active={isVict} />

        {/* 5 — Marcadores de etapa, siempre visibles a la derecha */}
        <StageMarkers visible={visible} />

        {/* 6 — Spark burst sobre la moneda más alta */}
        {lastDonor && (
          <SparkBurst
            key={lastDonor.id || lastDonor.name}
            x={topCoin.x} y={topCoin.y - 2}
            seed={lastDonor.id ? lastDonor.id.length : 42}
          />
        )}

        {/* 7 — Personaje siempre visible, encima de todo (incluyendo el lienzo) */}
        <RescuePerson isVictory={isVict} />
      </svg>

      {/* Badge última donación — oculto en victoria para no tapar el lienzo */}
      {lastDonor && !isVict && (
        <div key={lastDonor.id || lastDonor.name} style={{
          position: "absolute", top: "44%", left: "8%",
          background: "rgba(11,10,8,.88)", border: "1.5px solid var(--gold)",
          borderRadius: 12, padding: "10px 14px", color: "#faf8f3",
          backdropFilter: "blur(6px)",
          animation: "donation-glow 1.8s ease-in-out infinite, fadeUp .6s cubic-bezier(.2,.7,.2,1) both",
          maxWidth: 190, zIndex: 6,
        }}>
          <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 10, letterSpacing: "0.06em", color: "#E8A93B", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#E8A93B" }} />
            Último aporte
          </div>
          <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 13, fontWeight: 700, marginTop: 4 }}>
            {lastDonor.name}
          </div>
          <div style={{ fontFamily: "Anton, sans-serif", fontSize: 22, color: "#E8A93B", marginTop: 2, letterSpacing: "0.01em" }}>
            {lastDonor.amount}
          </div>
          <div aria-hidden="true" style={{ position: "absolute", left: "50%", top: "50%", width: 0, height: 0, pointerEvents: "none" }}>
            {[{ c: "#E8A93B", dx: -88, dy: -26, dr: -45, d: 0 }, { c: "#E63946", dx: -64, dy: 22, dr: 30, d: .05 },
              { c: "#fff5d6", dx: -52, dy: -40, dr: -20, d: .1 }, { c: "#E8A93B", dx: -32, dy: 26, dr: 45, d: .15 },
              { c: "#ffb27e", dx: 96, dy: -20, dr: 25, d: 0 }, { c: "#E8A93B", dx: 74, dy: 20, dr: -45, d: .08 },
              { c: "#fff5d6", dx: 60, dy: -32, dr: 60, d: .12 }, { c: "#E63946", dx: 40, dy: 28, dr: -15, d: .18 },
            ].map((p, k) => (
              <span key={k} style={{
                position: "absolute", left: 0, top: 0, width: 6, height: 8, borderRadius: 1,
                background: p.c, animation: `confetti-fly 1.4s cubic-bezier(.2,.7,.2,1) forwards`,
                animationDelay: `${p.d}s`, "--cx": `${p.dx}px`, "--cy": `${p.dy}px`, "--cr": `${p.dr}deg`,
              }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
